import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  X,
  Bold,
  Italic,
  List as ListIcon,
  Link as LinkIcon,
  AlignLeft,
  AlignCenter,
  AlignRight
} from 'lucide-react';
import toast from 'react-hot-toast';
import SurveyBuilder from '../Survey/SurveyBuilder';
import studyBuilderService from '../../services/studyBuilderService';
import surveyService from '../../services/surveyService';
import reminderService from '../../services/reminderService';

/**
 * LibraryBuilderModal is used to create or edit:
 *   - standard_survey
 *   - missing_form
 *   - email_reminder
 *   - push_reminder
 *
 * When done, it calls `onLibraryUpdated()` (from parent) to refresh the parent's lists.
 */
const LibraryBuilderModalComponent = ({
  isOpen,
  onClose,
  type,
  currentStudyId,
  currentStudyDraftId,
  studyName,
  editingSurvey,
  editingReminder,
  globalLibrary = false, // Added globalLibrary flag
  onLibraryUpdated // <-- callback from parent to refresh lists after creation/update
}) => {
  const [reminderType, setReminderType] = useState(type === 'push_notification' ? 'push' : 'email');
  const [reminderContent, setReminderContent] = useState('');
  const [reminderName, setReminderName] = useState('');
  const [reminderSubject, setReminderSubject] = useState('');
  const [formatSelection, setFormatSelection] = useState({ start: 0, end: 0 });
  const [loading, setLoading] = useState(false);
  
  // Add state to hold existing items for name validation
  const [existingSurveys, setExistingSurveys] = useState([]);
  const [existingMissingForms, setExistingMissingForms] = useState([]);
  const [existingEmailReminders, setExistingEmailReminders] = useState([]);
  const [existingPushReminders, setExistingPushReminders] = useState([]);
  const [existingTemplateNames, setExistingTemplateNames] = useState([]);

  // Load existing items for validation immediately when modal opens
  useEffect(() => {
    const fetchExistingItems = async () => {
      try {
        if (globalLibrary) {
          // For global library, fetch existing global surveys and reminders
          if (type === 'standard' || type === 'missing_form') {
            const surveyResponse = await surveyService.getGlobalSurveys(type);
            if (surveyResponse && surveyResponse.data) {
              console.log(`Fetched global ${type} surveys for validation:`, surveyResponse.data);
              if (type === 'standard') {
                setExistingSurveys(surveyResponse.data || []);
              } else {
                setExistingMissingForms(surveyResponse.data || []);
              }
            }
          } else if (type === 'email' || type === 'push_notification') {
            const channel = type === 'email' ? 'email' : 'push';
            const remindersResponse = await reminderService.getGlobalReminders(channel);
            if (remindersResponse && remindersResponse.data) {
              console.log(`Fetched global ${channel} reminders for validation:`, remindersResponse.data);
              if (channel === 'email') {
                setExistingEmailReminders(remindersResponse.data || []);
              } else {
                setExistingPushReminders(remindersResponse.data || []);
              }
            }
          }
        } else if (studyName) {
          // Study-specific library - use existing code for fetching
          // Fetch existing standard surveys and missing forms
          const surveyResponse = await surveyService.getSurveysByStudyId(studyName);
          if (surveyResponse && surveyResponse.surveys) {
            const allSurveys = surveyResponse.surveys || [];
            console.log('Fetched surveys for validation:', allSurveys);
            setExistingSurveys(allSurveys.filter(s => s.survey_type === 'standard'));
            setExistingMissingForms(allSurveys.filter(s => s.survey_type === 'missing_form'));
          }
        
          if (currentStudyDraftId) {
            // Fetch existing reminders
            const reminders = await reminderService.getReminderTemplates(currentStudyDraftId);
            const validReminders = Array.isArray(reminders) ? reminders : [];
            console.log('Fetched reminders for validation:', validReminders);
            
            setExistingEmailReminders(validReminders.filter(r => r && r.channel === 'email'));
            setExistingPushReminders(validReminders.filter(r => r && r.channel === 'push'));
          }
        }
      } catch (err) {
        console.error('Error fetching existing items for validation:', err);
      }
    };
    
    // Only fetch when modal is open
    if (isOpen) {
      fetchExistingItems();
    }
  }, [studyName, currentStudyDraftId, isOpen, globalLibrary, type]);

  // Add useEffect to load all template names when the component loads or when type changes
  useEffect(() => {
    if ((type === 'email_reminder' || type === 'push_reminder' || type === 'push_notification') && isOpen) {
      fetchAllTemplateNames();
    }
  }, [type, isOpen, globalLibrary, currentStudyDraftId]);

  // Function to fetch all existing template names
  const fetchAllTemplateNames = async () => {
    try {
      let emailNames = [];
      let pushNames = [];

      if (globalLibrary) {
        // For global library templates
        const emailResponse = await reminderService.getGlobalReminders('email');
        const pushResponse = await reminderService.getGlobalReminders('push');
        
        emailNames = (emailResponse?.data || []).map(item => ({
          id: item.id,
          name: item.name?.toLowerCase(),
          type: 'email'
        }));
        
        pushNames = (pushResponse?.data || []).map(item => ({
          id: item.id,
          name: item.name?.toLowerCase(),
          type: 'push'
        }));
      } else if (currentStudyDraftId) {
        // For study-specific templates
        const reminders = await reminderService.getReminderTemplates(currentStudyDraftId);
        const validReminders = Array.isArray(reminders) ? reminders : [];
        
        emailNames = validReminders
          .filter(r => r && r.channel === 'email')
          .map(item => ({
            id: item.id,
            name: item.name?.toLowerCase(),
            type: 'email'
          }));
        
        pushNames = validReminders
          .filter(r => r && r.channel === 'push')
          .map(item => ({
            id: item.id,
            name: item.name?.toLowerCase(),
            type: 'push'
          }));
      }

      const allNames = [...emailNames, ...pushNames];
      console.log('All existing template names:', allNames);
      setExistingTemplateNames(allNames);
    } catch (err) {
      console.error('Error fetching template names for validation:', err);
    }
  };

  // If editing a reminder, load its data into local state:
  useEffect(() => {
    if (editingReminder) {
      setReminderName(editingReminder.name || '');
      setReminderContent(editingReminder.template_content || '');
      setReminderType(editingReminder.channel === 'push' ? 'push' : 'email');
      setReminderSubject(editingReminder.metadata?.subject || '');
    } else {
      // If no editingReminder, set defaults based on type (push/email)
      if (type === 'email') {
        setReminderType('email');
      } else if (type === 'push_notification') {
        setReminderType('push');
      }
      setReminderName('');
      setReminderContent('');
      setReminderSubject('');
    }
  }, [editingReminder, type]);

  // For debugging:
  useEffect(() => {
    console.log('LibraryBuilderModal: studyName =', studyName);
    console.log('LibraryBuilderModal: currentStudyDraftId =', currentStudyDraftId);
    console.log('LibraryBuilderModal: globalLibrary =', globalLibrary);
  }, [studyName, currentStudyDraftId, globalLibrary]);

  const handleSaveSurvey = async (data) => {
    try {
      setLoading(true);

      if (!globalLibrary && !studyName) {
        throw new Error('No valid study name found');
      }
      
      if (!data.name || !data.name.trim()) {
        throw new Error('Survey name is required');
      }
      
      // Check for duplicate names - always fetch the latest list to ensure accuracy
      const surveyName = data.name.trim();
      const isStandardSurvey = type !== 'missing_form';
      
      // Get fresh data to validate against - check the STUDY DRAFT, not the library
      let existingItems = [];
      
      if (globalLibrary) {
        // For global library, fetch latest global surveys for validation
        const surveyResponse = await surveyService.getGlobalSurveys(isStandardSurvey ? 'standard' : 'missing_form');
        existingItems = surveyResponse?.data || [];
      } else {
        // For study-specific library, check ALL surveys in the study draft (not just the library type)
        // This prevents creating surveys with duplicate names regardless of their type
        const surveyResponse = await surveyService.getSurveysByStudyId(studyName);
        if (surveyResponse && surveyResponse.surveys) {
          // Check against ALL surveys in the study, not just the same type
          existingItems = surveyResponse.surveys;
        }
      }
      
      console.log(`Checking for duplicates among ${existingItems.length} surveys in study draft`);
      
      const duplicateName = existingItems.find(item => 
        item.name?.toLowerCase() === surveyName.toLowerCase() && 
        (!editingSurvey || item.id !== editingSurvey.id)
      );
      
      if (duplicateName) {
        console.log('Found duplicate:', duplicateName);
        throw new Error(`A survey with the name "${surveyName}" already exists in this study. Please use a unique name.`);
      }

      // Prepare the survey data to send to the API
      const surveyData = {
        ...data,
      };
      
      if (!globalLibrary) {
        // For study-specific surveys
        surveyData.study_name = studyName;
      }
      
      // If editing an existing survey, preserve its existing properties
      if (editingSurvey) {
        if (!globalLibrary) {
          // Preserve the existing studies for study-specific library
          surveyData.studies = editingSurvey.studies;
        }
        surveyData.survey_type = editingSurvey.survey_type;
        surveyData.is_missing = editingSurvey.survey_type === 'missing_form';
      } else {
        // For new surveys, set type based on the modal type
        surveyData.survey_type = type === 'missing_form' ? 'missing_form' : 'standard';
        surveyData.is_missing = type === 'missing_form';
        
        if (!globalLibrary && currentStudyId) {
          surveyData.studies = [currentStudyId];
        }
      }

      console.log('Survey data before submission:', surveyData);
      for (var q of surveyData.questions) {
        if (q.text.trim() === '')
          throw new Error(`Question text cannot be empty.`);
      }

      // If editing an existing survey, update; otherwise create new.
      if (editingSurvey) {
        if (globalLibrary) {
          await surveyService.updateGlobalSurvey(editingSurvey.id, surveyData);
        } else {
          await surveyService.updateSurvey(editingSurvey.id, surveyData);
        }
        toast.success('Survey updated successfully!');
      } else {
        if (globalLibrary) {
          await surveyService.createGlobalSurvey(surveyData);
        } else {
          await surveyService.createSurvey(surveyData);
        }
        toast.success('Survey created successfully!');
      }

      // Pass to parent's callback if defined.
      if (onLibraryUpdated) {
        onLibraryUpdated(type);
      }

      // Finally close the modal
      onClose();
    } catch (err) {
      console.error(`Error ${editingSurvey ? 'updating' : 'creating'} survey:`, err);
      toast.error(err.message || `Failed to ${editingSurvey ? 'update' : 'create'} survey`);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveReminder = async () => {
    try {
      setLoading(true);

      if (!reminderName.trim()) {
        throw new Error('Reminder name is required');
      }
      if (!reminderContent.trim()) {
        throw new Error('Reminder content is required');
      }
      if (reminderType === 'email' && !reminderSubject.trim()) {
        throw new Error('Email subject is required');
      }
      if (!globalLibrary && !currentStudyDraftId) {
        throw new Error('No valid study draft ID found');
      }
      
      // Validate the name against all existing template names
      const trimmedName = reminderName.trim().toLowerCase();
      console.log('Validating name:', trimmedName);
      console.log('Existing template names:', existingTemplateNames);
      console.log('Editing reminder ID:', editingReminder?.id);
      
      // Check for duplicate name, excluding the current template if we're editing
      const duplicateName = existingTemplateNames.find(item => 
        item.name === trimmedName && 
        (!editingReminder || item.id !== editingReminder.id)
      );
      
      if (duplicateName) {
        console.log('Duplicate template found:', duplicateName);
        throw new Error(`A template with the name "${reminderName.trim()}" already exists as a ${duplicateName.type} template. Please use a unique name across all reminder types.`);
      }

      // If editing an existing reminder, do update. Else create new.
      if (editingReminder) {
        const updateData = {
          name: reminderName,
          channel: reminderType, // 'email' or 'push'
          template_content: reminderContent,
          metadata: {
            ...editingReminder.metadata,
            subject: reminderType === 'email' ? reminderSubject : undefined
          },
          default_schedule: editingReminder.default_schedule || [1, 3, 7],
          placeholders: editingReminder.placeholders || {
            required: ['participant_name', 'study_name', 'survey_link', 'due_date'],
            optional: []
          },
          is_active: editingReminder.is_active
        };

        if (globalLibrary) {
          await reminderService.updateGlobalReminder(editingReminder.id, updateData);
        } else {
          await reminderService.updateReminderTemplate(editingReminder.id, updateData);
        }
        toast.success('Reminder template updated successfully!');
      } else {
        // Create new reminder template
        const templateData = {
          name: reminderName,
          type,
          channel: reminderType, // 'email' or 'push'
          template_content: reminderContent,
          metadata: {
            subject: reminderType === 'email' ? reminderSubject : undefined
          },
          default_schedule: [1, 3, 7],
          placeholders: {
            required: ['participant_name', 'study_name', 'survey_link', 'due_date'],
            optional: []
          },
          is_active: true
        };

        if (globalLibrary) {
          await reminderService.createGlobalReminder(templateData);
          toast.success('Global reminder template created successfully!');
        } else {
          // For study-specific reminders
          templateData.study_draft_id = currentStudyDraftId;
          
          const template = await reminderService.createReminderTemplate(templateData);
          toast.success('Reminder template created successfully!');

          // Optionally schedule default reminders (example logic)
          const now = new Date();
          const targetDates = [1, 3, 7].map((days) => {
            const date = new Date(now);
            date.setDate(date.getDate() + days);
            return date;
          });

          await reminderService.scheduleReminders(template.id, currentStudyDraftId, targetDates, {
            participant_name: '${participant_name}',
            study_name: '${study_name}',
            survey_link: '${survey_link}',
            due_date: '${due_date}'
          });

          //toast.success('Default reminders scheduled successfully!');
        }
      }

      // Refresh parent lists after success.
      if (onLibraryUpdated) {
        onLibraryUpdated(reminderType === 'email' ? 'email' : 'push_notification');
      }

      // Close the modal.
      onClose();
    } catch (err) {
      console.error('Error saving reminder template:', err);
      toast.error(err.message || 'Failed to save reminder template');
    } finally {
      setLoading(false);
    }
  };

  // Helper to format text (bold, italic, etc.) in the reminder content (only relevant for reminders).
  const handleFormat = (format) => {
    const textArea = document.getElementById('reminder-content');
    if (!textArea) return;

    const start = textArea.selectionStart;
    const end = textArea.selectionEnd;
    const selectedText = reminderContent.substring(start, end);
    let newContent = reminderContent;

    switch (format) {
      case 'bold':
        newContent =
          reminderContent.slice(0, start) +
          '**' +
          selectedText +
          '**' +
          reminderContent.slice(end);
        break;
      case 'italic':
        newContent =
          reminderContent.slice(0, start) +
          '_' +
          selectedText +
          '_' +
          reminderContent.slice(end);
        break;
      case 'list': {
        const lines = selectedText.split('\n').map((line) => `- ${line}`);
        newContent =
          reminderContent.slice(0, start) + lines.join('\n') + reminderContent.slice(end);
        break;
      }
      case 'link': {
        const url = 'https://example.com';
        const linkText = selectedText || 'Link Text';
        newContent =
          reminderContent.slice(0, start) +
          `[${linkText}](${url})` +
          reminderContent.slice(end);
        break;
      }
      case 'align-left': {
        const lines = selectedText.split('\n').map((line) => `> ${line}`);
        newContent =
          reminderContent.slice(0, start) +
          lines.join('\n') +
          reminderContent.slice(end);
        break;
      }
      case 'align-center':
        newContent =
          reminderContent.slice(0, start) +
          `<center>${selectedText}</center>` +
          reminderContent.slice(end);
        break;
      case 'align-right':
        newContent =
          reminderContent.slice(0, start) +
          `<div style="text-align:right;">${selectedText}</div>` +
          reminderContent.slice(end);
        break;
      default:
        // no-op
        break;
    }

    setReminderContent(newContent);

    // Re-focus the textarea after formatting:
    requestAnimationFrame(() => {
      textArea.focus();
      const newPos = start + (newContent.length - reminderContent.length);
      textArea.setSelectionRange(newPos, newPos);
    });
  };

  // Render the UI for building an email/push reminder template.
  const renderReminderTemplate = () => (
    <div className="space-y-6">
      <div>
        <label htmlFor="reminderName" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
          Template Name<span className="text-red-500 ml-1">*</span>
        </label>
        <input
          type="text"
          id="reminderName"
          value={reminderName}
          onChange={(e) => setReminderName(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Enter template name"
          required
        />
      </div>

      {reminderType === 'email' && (
        <div>
          <label htmlFor="reminderSubject" className="block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1">
            Email Subject<span className="text-red-500 ml-1">*</span>
          </label>
          <input
            type="text"
            id="reminderSubject"
            value={reminderSubject}
            onChange={(e) => setReminderSubject(e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary-500"
          placeholder="Enter email subject"
            required
          />
        </div>
      )}

      <div className="space-y-4">
        <div className="border border-gray-200 dark:border-gray-700 rounded-lg p-2">
          {/* Buttons row for formatting */}
          <div className="flex space-x-2 border-b border-gray-200 dark:border-gray-700 pb-2 mb-2">
            <button onClick={() => handleFormat('bold')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
              <Bold size={18} />
            </button>
            <button onClick={() => handleFormat('italic')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
              <Italic size={18} />
            </button>
            <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-1" />
            <button onClick={() => handleFormat('list')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
              <ListIcon size={18} />
            </button>
            <button onClick={() => handleFormat('link')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded">
              <LinkIcon size={18} />
            </button>
            <div className="h-6 w-px bg-gray-300 dark:bg-gray-600 mx-1" />
            <button
              onClick={() => handleFormat('align-left')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              <AlignLeft size={18} />
            </button>
            <button
              onClick={() => handleFormat('align-center')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              <AlignCenter size={18} />
            </button>
            <button
              onClick={() => handleFormat('align-right')}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
            >
              <AlignRight size={18} />
            </button>
          </div>

          {/* Main content textarea */}
          <textarea
            id="reminder-content"
            value={reminderContent}
            onChange={(e) => setReminderContent(e.target.value)}
            className="w-full h-64 p-2 focus:outline-none resize-none"
            placeholder={
              reminderType === 'email'
                ? 'Enter your email template content...'
                : 'Enter your push notification message...'
            }
          />
        </div>

        {reminderType === 'email' && (
          <div className="bg-gray-50 dark:bg-gray-900 p-4 rounded-lg">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-2">Available Variables:</h3>
            <div className="grid grid-cols-2 gap-2">
              {[
                'user_name',
                'user_role',
                'user_email',
                'study_name',
                'noncompliant_participant_list',
                'compliant_participant_list'
              ].map((variable) => (
                <button
                  key={variable}
                  onClick={() => {
                    const textArea = document.getElementById('reminder-content');
                    if (textArea) {
                      const cursorPos = textArea.selectionStart;
                      const variableText = `{{${variable}}}`;
                      const newContent = 
                        reminderContent.slice(0, cursorPos) + 
                        variableText + 
                        reminderContent.slice(cursorPos);
                      setReminderContent(newContent);
                      // Set cursor position after the inserted variable
                      requestAnimationFrame(() => {
                        textArea.focus();
                        const newPos = cursorPos + variableText.length;
                        textArea.setSelectionRange(newPos, newPos);
                      });
                    }
                  }}
                  className="text-sm text-gray-600 dark:text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 px-2 py-1 rounded text-left w-full"
                >
                  {'{{' + variable + '}}'}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  const getModalTitle = () => {
    if (editingSurvey) {
      return editingSurvey.survey_type === 'missing_form' ? 'Edit Missing Form' : 'Edit Survey';
    } else if (type === 'standard_survey') {
      return 'Create Survey';
    } else if (type === 'missing_form') {
      return 'Create Missing Form';
    } else if (type === 'email_reminder') {
      return editingReminder ? 'Edit Email Reminder' : 'Create Email Reminder';
    } else {
      return editingReminder ? 'Edit Push Notification' : 'Create Push Notification';
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-screen items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-25 transition-opacity"
              onClick={onClose}
            />
            
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="relative w-full max-w-2xl transform rounded-lg bg-white dark:bg-gray-800 shadow-xl dark:shadow-gray-900/20 transition-all max-h-[90vh] flex flex-col"
            >
              {/* Header */}
              <div className="px-6 py-4 bg-primary-600 dark:bg-primary-700 flex justify-between items-center flex-shrink-0 rounded-t-lg">
                <h3 className="text-xl font-semibold text-white">
                  {getModalTitle()}
                </h3>
                <button
                  onClick={onClose}
                  className="text-white hover:text-gray-200 transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Body / Content */}
              <div className="p-6 overflow-y-auto flex-grow">

                {(type === 'standard_survey' || type === 'missing_form') ? (
                  // Renders the SurveyBuilder for surveys or missing forms
                  <SurveyBuilder
                    survey={editingSurvey}
                    surveyType={type === 'missing_form' ? 'missing_form' : 'standard'}
                    onCreateSurvey={(data) =>
                      handleSaveSurvey({
                        ...data,
                        is_missing: type === 'missing_form'
                      })
                    }
                    onUpdateSurvey={(data) =>
                      handleSaveSurvey({
                        ...data,
                        is_missing: type === 'missing_form'
                      })
                    }
                    onCancel={onClose}
                    loading={loading}
                    initialStudyId={currentStudyId}
                  />
                ) : (
                  // Renders the reminder template builder for email/push reminders
                  renderReminderTemplate()
                )}
              </div>

              {/* Footer (for reminders only) */}
              {(type === 'email_reminder' || type === 'push_reminder' || type === 'push_notification') && (
                <div className="px-6 py-4 bg-gray-50 dark:bg-gray-900 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-4 flex-shrink-0 rounded-b-lg">
                  <button
                    onClick={onClose}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSaveReminder}
                    disabled={loading}
                    className="px-4 py-2 bg-primary-600 text-white rounded-md text-sm font-medium hover:bg-primary-700 disabled:opacity-50"
                  >
                    {editingReminder
                      ? (loading ? 'Updating...' : 'Update Template')
                      : (loading ? 'Creating...' : 'Create Template')}
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        </div>
      )}
    </AnimatePresence>
  );
};

export default LibraryBuilderModalComponent;